import {
  get as apiGet, post as apiPost, put as apiPut, TYPE_BROOKLYN, REQUEST_TYPE_APPLICATION_JSON,
} from 'api-client'
import {
  client as apolloClient,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from 'services/apollo-graphql'
import { fromNow } from 'services/date-time'
import strictUriEncode from 'strict-uri-encode'
import _set from 'lodash/set'
import _get from 'lodash/get'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _isArray from 'lodash/isArray'
import _parseInt from 'lodash/parseInt'
import _map from 'lodash/map'
import _reduce from 'lodash/reduce'
import _findIndex from 'lodash/findIndex'
import _filter from 'lodash/filter'
import _times from 'lodash/times'
import _concat from 'lodash/concat'
import {
  COMMENT_LIST_QUERY,
  COMMENT_CREATE_MUTATION,
  COMMENT_UPDATE_MUTATION,
  COMMENT_REPORT_MUTATION,
} from './queries'

const SCHEMA_COMMENT = {
  cid: null,
  comment: null,
  format: null,
  homepage: null,
  mail: null,
  name: null,
  picture: null,
  pid: null,
  registered_name: null,
  signature: null,
  signature_format: null,
  status: null,
  subject: null,
  thread: null,
  timestamp: null,
  uid: null,
}

export const TYPE_PLACEHOLDER = 'TYPE_PLACEHOLDER'

export const COMMENT_STATUS_PUBLISHED = 0
export const COMMENT_STATUS_UNPUBLISHED = 1
export const COMMENT_STATUS_NEEDS_REVIEW = 2

const SCHEMA = {
  currentPage: null,
  totalCount: null,
  comments: [SCHEMA_COMMENT],
}

export async function get (options) {
  const {
    id, auth, page = 0, limit = 250,
  } = options
  const apiQuery = {
    p: page,
    pp: limit,
  }
  const res = await apiGet(
    `s/node/comments/${strictUriEncode(id)}`,
    apiQuery,
    { auth },
    TYPE_BROOKLYN,
  )
  return handleGetResponse(res, id)
}

export function handleGetResponse (res, id, _dataError) {
  const data = _get(res, 'body', {})
  const comments = _isArray(_get(data, 'comments'))
    ? _get(data, 'comments')
    : []
  const commentsSorted = _map(comments, formatCommentResponse).sort(
    sortComments,
  )
  const parentComments = _filter(commentsSorted, { pid: 0 })
  const childrenComments = _filter(
    commentsSorted,
    (comment) => _get(comment, 'pid') > 0,
  )
  return _assign(_cloneDeep(SCHEMA), {
    currentPage: _parseInt(_get(data, 'currentPage', 1)),
    totalCount: _parseInt(_get(data, 'totalCount', 0)),
    id,
    _dataError,
    comments: _reduce(
      childrenComments,
      reduceChildrenToParentThreads,
      parentComments,
    ),
  })
}

function reduceChildrenToParentThreads (parents, child) {
  const pid = _get(child, 'pid')
  const parentIndex = _findIndex(parents, { cid: pid })
  // Find a parent and attach the child, removing the child from the flat list
  if (parentIndex > -1) {
    const children = _get(parents, [parentIndex, 'children'], [])
    _set(parents, [parentIndex, 'children'], _concat(children, child))
  }
  return parents
}

function sortComments (commentA, commentB) {
  const timestampA = _get(commentA, ['timestamp'])
  const timestampB = _get(commentB, ['timestamp'])
  if (timestampA < timestampB) {
    return 1
  }
  return -1
}

export async function create (options) {
  const { id, auth, data } = options
  const cid = _get(data, 'cid')
  // editing a comment
  if (cid) {
    const comment = _get(data, 'body')
    const uid = _get(data, 'uid')
    const putData = {
      comment,
      status: COMMENT_STATUS_PUBLISHED,
      uid,
    }
    const putOptions = {
      auth,
      reqType: REQUEST_TYPE_APPLICATION_JSON,
    }
    apiPut(
      `v1/comments/${cid}`,
      putData,
      putOptions,
      TYPE_BROOKLYN,
    )
    return null
  }

  const res = await apiPost(`s/node/comments/${strictUriEncode(id)}`, data, { auth }, TYPE_BROOKLYN)
  return handleCreateResponse(res)
}

function handleCreateResponse (res) {
  const commentData = _get(res, ['body', 'comment'], {})
  _set(commentData, 'registered_name', _get(commentData, 'name', ''))
  return formatCommentResponse(commentData)
}

function formatCommentResponse (comment) {
  const timestamp = _parseInt(_get(comment, 'timestamp', 0)) * 1000
  return _assign(_cloneDeep(SCHEMA_COMMENT), comment, {
    timestamp,
    createdDateTimeText: fromNow(timestamp),
    uid: _parseInt(_get(comment, 'uid', -1)),
    pid: _parseInt(_get(comment, 'pid', -1)),
    cid: _parseInt(_get(comment, 'cid', -1)),
    contentId: _parseInt(_get(comment, 'nid', -1)),
  })
}

export function getPlaceholders (count = 2) {
  return _times(count, () => _assign(_cloneDeep(SCHEMA_COMMENT), {
    comment: '',
    type: TYPE_PLACEHOLDER,
    pid: 0,
    cid: -1,
  }))
}

export async function update ({ commentId, auth, data }) {
  const res = await apiPut(
    `v1/comments/${commentId}`,
    data,
    { auth, reqType: REQUEST_TYPE_APPLICATION_JSON },
    TYPE_BROOKLYN,
  )

  return res.body
}

// GraphQL status enum to integer mapping
const GRAPHQL_STATUS_MAP = {
  PUBLISHED: COMMENT_STATUS_PUBLISHED,
  UNPUBLISHED: COMMENT_STATUS_UNPUBLISHED,
  NEEDS_REVIEW: COMMENT_STATUS_NEEDS_REVIEW,
}

/**
 * Transform a GraphQL comment into the existing Redux state shape
 * @param {Object} gqlComment A comment from the GraphQL response
 * @returns {Object} Comment in the Redux/Drupal format
 */
function formatGraphQLComment (gqlComment) {
  const timestamp = new Date(gqlComment.createdAt).getTime()
  const uid = _parseInt(_get(gqlComment, 'uid', -1))
  const avatarSmall = _get(gqlComment, ['userInfoPublic', 'avatar', 'small'], '')
  const authorUid = _get(gqlComment, ['userInfoPublic', 'uid'])
  return _assign(_cloneDeep(SCHEMA_COMMENT), {
    cid: _parseInt(gqlComment.id),
    comment: gqlComment.comment || '',
    subject: gqlComment.subject || '',
    uid,
    pid: _parseInt(_get(gqlComment, 'pid', 0)),
    contentId: _parseInt(_get(gqlComment, 'contentId', -1)),
    timestamp,
    createdDateTimeText: fromNow(timestamp),
    picture: avatarSmall,
    registered_name: authorUid ? String(authorUid) : '',
    status: String(_get(GRAPHQL_STATUS_MAP, gqlComment.status, 0)),
    children: _map(
      _get(gqlComment, 'commentsChildren', []),
      formatGraphQLComment,
    ),
  })
}

/**
 * Get comments via GraphQL
 * @param {Object} options The options
 * @param {Number} options.contentId The content ID
 * @param {String} options.auth JWT auth token
 * @param {Number} [options.limit] The limit
 * @param {Number} [options.offset] The offset
 */
export async function getGraphQL (options) {
  const {
    contentId, auth, limit = 250, offset = 0,
  } = options
  const { data, error } = await apolloClient.query({
    query: COMMENT_LIST_QUERY,
    variables: { contentId: _parseInt(contentId), limit, offset },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt: auth },
  })
  if (error || !data) {
    return _assign(_cloneDeep(SCHEMA), { id: contentId, _dataError: error })
  }
  const gqlComments = _get(data, ['commentList', 'comments'], [])
  const count = _get(data, ['commentList', 'count'], 0)
  const comments = _map(gqlComments, formatGraphQLComment)
  return _assign(_cloneDeep(SCHEMA), {
    id: contentId,
    currentPage: 1,
    totalCount: count,
    comments,
  })
}

/**
 * Create a comment via GraphQL
 * @param {Object} options The options
 * @param {Number} options.contentId The content ID
 * @param {String} options.auth JWT auth token
 * @param {String} options.body The comment body
 * @param {String} [options.subject] The subject
 * @param {Number} [options.pid] Parent comment ID
 */
export async function createGraphQL (options) {
  const {
    contentId, auth, body, subject, pid,
  } = options
  const input = {
    contentId: _parseInt(contentId),
    comment: body,
    subject: subject || '',
    pid: pid ? _parseInt(pid) : 0,
  }
  const { data } = await apolloClient.mutate({
    mutation: COMMENT_CREATE_MUTATION,
    variables: { input },
    context: { jwt: auth },
  })
  const gqlComment = _get(data, ['commentCreate', 'comment'])
  if (!gqlComment) return null
  return formatGraphQLComment(gqlComment)
}

/**
 * Update a comment via GraphQL
 * @param {Object} options The options
 * @param {String} options.commentId The comment ID
 * @param {String} options.auth JWT auth token
 * @param {String} options.comment The updated comment body
 * @param {String} [options.subject] The updated subject
 */
export async function updateGraphQL (options) {
  const {
    commentId, auth, comment, subject,
  } = options
  const input = { id: String(commentId), comment }
  if (subject !== undefined) {
    input.subject = subject
  }
  const { data } = await apolloClient.mutate({
    mutation: COMMENT_UPDATE_MUTATION,
    variables: { input },
    context: { jwt: auth },
  })
  return _get(data, ['commentUpdate', 'success'], false)
}

/**
 * Report a comment via GraphQL
 * @param {Object} options The options
 * @param {String} options.commentId The comment ID
 * @param {String} options.auth JWT auth token
 */
export async function reportGraphQL (options) {
  const { commentId, auth } = options
  const { data } = await apolloClient.mutate({
    mutation: COMMENT_REPORT_MUTATION,
    variables: { id: String(commentId) },
    context: { jwt: auth },
  })
  return _get(data, ['commentReport', 'success'], false)
}
